<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          Custom Print Orders
        </h1>
        <a
          v-if="officeAccess"
          @click="$router.push('/print-order-form')"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Order Another Product
        </a>
      </div>
    </div>

    <loader v-if="!loaded || savingOrder" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to use the print order form.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col py-6 px-4">
      <div class="flex flex-wrap items-center leading-none pb-6 border-b-2 border-grey-light">
        <h3 class="flex w-full mb-4 lg:w-1/3 lg:mb-0">Custom Print Orders - Basket</h3>
        <div class="w-full lg:w-2/3">
          <print-sub-menu />
        </div>
      </div>
      <div class="flex flex-wrap mt-8">
        <div class="mb-6 w-full lg:w-2/3">
          <div class="border border-grey-light">

            <!-- STAGE TABS -->
            <div class="w-full flex text-sm mb-6 bg-grey-lighter">
              <div @click="stage = Math.min(stage, i)" class="flex flex-col w-1/3" :class="{'border-l border-r': i === 1, 'cursor-pointer': i < stage}" v-for="(stageTab, i) in stages" :key="i">
                <div class="w-full h-full px-3 pt-3 pb-5 border-t-2" :class="[(i === stage ? 'border-t-2 border-print-order-teal bg-white' : 'border-transparent bg-grey-lighter')]">
                  <div class="flex h-full items-center">
                    <i class="fas ml-1 mr-2 text-lg text-print-order-teal" :class="stageTab.icon" />
                    <div class="text-sm">{{ stageTab.name }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-yellow-lighter flex p-4 mx-4 mb-4 rounded text-sm leading-normal" v-if="!isEmpty(errorBag.errors)">
              <i class="fas fa-exclamation-triangle mr-3 fa-2x pt-2"></i>
              <div class="flex-grow">
                <strong class="block mb-3 text-sm">There were problems with your order. Please resolve these issues and try again.</strong>
                <ul class="pl-6">
                  <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
                </ul>
              </div>
              <i class="fas fa-times text-xl ml-3 text-grey-darker hover:text-print-order-teal cursor-pointer" @click="errorBag.clear()"></i>
            </div>

            <div class="w-full text-sm mb-2 p-4">
              <!-- STAGE FORM -->
              <component :is="stageView" v-model="order" @updateAddress="updateDeliveryAddress" :deliveryAddressId="order.delivery_address_id" :deliveryAddress="deliveryAddress" :deliveryAddresses="deliveryAddresses" :requiresDeliveryAddress="requiresDeliveryAddress" :additionalDelivery="additionalDelivery" :basketItems="order.items" :notificationCc="order.notification_cc" :updatingItem="updatingItem" :subtotal="subtotal" :vat="vat" :total="total" :receipt="receipt" :errorBag="errorBag" :discountType="discountType" @removeFromBasket="removeFromBasket" @continueShopping="continueShopping" @addCc="addCc" @removeCc="removeCc" @updatePhoneNumber="updatePhoneNumber" @loadBasket="loadBasket" />
            </div>
          </div>
        </div>

        <PrintOrderBasketSummary :stage="stage" :basketItems="order.items" :subtotal="subtotal" :vat="vat" :total="total" :requiresDeliveryAddress="requiresDeliveryAddress" :additionalDelivery="additionalDelivery" @nextStep="nextStep" @placeOrder="placeOrder" @continueShopping="continueShopping" />

      </div>
    </div>

    <modal v-if="displayCreateOrderModal" @leave="displayCreateOrderModal = false" show-close-btn>
      <div v-if="paymentTypes.length > 1">
        <h2 class="font-bold text-base mb-6">Please select a payment type</h2>

        <p class="w-full flex items-center justify-between text-sm mb-4" v-for="(type, i) in paymentTypes" :key="i">
          <input type="radio" :id="`type${i}`" v-model="order.payment_type_id" :value="type" />
          <label :for="`type${i}`" class="flex-grow">{{ getPaymentTypeLabel(type) }}</label>
        </p>
      </div>

      <h2 class="font-bold text-base mb-6" :class="{'mt-8': paymentTypes.length > 1}">Terms &amp; Conditions</h2>

      <div class="border p-3 h-48 overflow-y-auto leading-normal page-content my-6 text-sm" v-html="terms.content"></div>

      <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 text-sm">
        <input type="checkbox" id="terms" v-model="order.accept_terms">
        <label for="terms">I accept the terms and conditions</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('accept_terms')" v-text="errorBag.get('accept_terms')"></small>
      </div>

      <div class="flex justify-end mt-6">
        <button class="w-full py-4 text-xs border focus:outline-none"
          :class="order.payment_type_id === null || !order.accept_terms || savingOrder ? 'cursor-default bg-grey-lighter text-grey-dark hover:bg-grey-lighter hover:text-grey-dark' : 'border-print-order-teal bg-print-order-teal-lightest hover:bg-print-order-teal text-print-order-teal hover:text-white'"
          :disabled="order.payment_type_id === null || !order.accept_terms || savingOrder" @click="createOrder()">
          Place Order &gt;&gt;
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import api, { v2Api } from '@/http'
import { isEmpty } from 'lodash'
import ErrorBag from '@/ErrorBag'
import PrintSubMenu from './print-order-form/PrintSubMenu'
import PrintOrderBasketSummary from './print-order-form/basket/PrintOrderBasketSummary'
import BasketStage0 from './print-order-form/basket/BasketStage0'
import BasketStage1 from './print-order-form/basket/BasketStage1'
import BasketStage2 from './print-order-form/basket/BasketStage2'
import { mapState, mapActions } from 'vuex'
export default {
  components: {
    PrintSubMenu,
    BasketStage0,
    BasketStage1,
    BasketStage2,
    PrintOrderBasketSummary
  },
  data () {
    return {
      loaded: false,
      officeAccess: true,
      paymentTypes: [],
      deliveryAddresses: [],
      deliveryAddress: null,
      additionalDelivery: null,
      terms: null,
      order: {
        office_id: null,
        payment_type_id: null,
        delivery_address_id: null,
        notification_cc: [],
        items: [],
        accept_terms: false
      },
      receipt: null,
      stage: 0,
      stages: [
        {
          name: 'Basket - Order Details',
          icon: 'fa-shopping-cart'
        },
        {
          name: 'Delivery',
          icon: 'fa-truck'
        },
        {
          name: 'Order Success',
          icon: 'fa-check-circle'
        }
      ],
      subtotal: 0,
      vat: 0,
      total: 0,
      errorBag: new ErrorBag(),
      savingOrder: false,
      updatingItem: false,
      displayCreateOrderModal: false,
      orderResponse: null,
      discountType: []
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile', 'system']),
    stageView () {
      return `basket-stage${this.stage}`
    },
    requiresDeliveryAddress () {
      return this.order.items.filter((item) => item.requires_delivery_address).length > 0
    }
  },
  watch: {
    selectedOffice: {
      handler () {
        this.loadPage()
      },
      deep: true
    }
  },
  created () {
    this.loadPage()
  },
  methods: {
    isEmpty,
    ...mapActions([
      'displayToastNotification'
    ]),
    loadPage () {
      this.loaded = false
      this.order.office_id = this.selectedOffice.id
      Promise.all([
        this.loadBasket(),
        this.loadDeliveryAddresses(),
        this.loadTerms()
      ]).then(() => {
        this.loaded = true
      })
    },
    loadBasket () {
      return v2Api.get('custom-print-order-items', {
        params: {
          office_id: this.selectedOffice.id,
          status_id: 1 // 'Basket' status
        }
      }).then((response) => {
        this.order.items = response.data.items
        this.officeAccess = response.data.access
        this.paymentTypes = response.data.payment_types
        this.discountType = response.data.discount_types

        this.calculateTotals()
      }).catch(() => {
        this.officeAccess = false
      }).finally(() => {
        this.loaded = true
      })
    },
    loadDeliveryAddresses () {
      return api.get('delivery-addresses', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.deliveryAddresses = response.data
        this.setDefaultAddress()
      })
    },
    loadTerms () {
      return v2Api.get('terms/latest', {
        params: {
          system: 'members',
          system_id: this.system.id,
          type_id: 4 // 'Print Order Form' type
        }
      }).then((response) => {
        this.terms = response.data
      })
    },
    updateDeliveryAddress (id) {
      this.order.delivery_address_id = id
    },
    setDefaultAddress () {
      for (let i = 0; i < this.deliveryAddresses.length; i++) {
        if (this.deliveryAddresses[i].preferred === true) {
          this.order.delivery_address_id = this.deliveryAddresses[i].id
          this.deliveryAddress = this.deliveryAddresses[i].address
          return
        }
      }
      this.order.delivery_address_id = null
      this.deliveryAddress = null
    },
    addCc (recipient) {
      if (!recipient) {
        return
      }

      this.order.notification_cc.push(recipient)
    },
    removeCc (recipient) {
      this.order.notification_cc.splice(this.order.notification_cc.indexOf(recipient), 1)
    },
    calculateTotals () {
      if (!this.order.items || this.order.items.length <= 0) {
        this.subtotal = 0
        this.vat = 0
        this.total = 0

        return
      }
      let subtotal = 0.0
      for (let i = 0; i < this.order.items.length; i++) {
        subtotal += this.order.items[i].item_quote
        subtotal += this.order.items[i].artwork_quote
        subtotal += this.order.items[i].delivery_quote
        if (this.order.items[i].sale_item.additional_delivery_charges_apply) {
          this.additionalDelivery = true
        }
      }
      this.subtotal = subtotal
      this.vat = this.selectedOffice.network_id === 8 ? 0 : subtotal * 0.2 // No VAT for international orders
      this.total = subtotal + this.vat
    },
    nextStep () {
      if (!this.order.items || this.order.items.length === 0) {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation',
          title: 'Error',
          info: 'Print order basket is empty'
        })
      } else if (this.stage < 2) {
        this.stage++
      }
    },
    placeOrder () {
      if (!this.paymentTypes || this.paymentTypes.length < 1) {
        return this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation',
          title: 'Error',
          info: 'Order could not be placed'
        })
      }
      if (this.paymentTypes.length === 1) {
        this.order.payment_type_id = this.paymentTypes[0]
      }
      this.displayCreateOrderModal = true
    },
    createOrder () {
      this.errorBag.clear()
      this.savingOrder = true
      this.order.phone_number = this.phoneNumber
      v2Api.post('/custom-print-orders', this.order).then((response) => {
        if (response.data.checkout_url) {
          window.location = response.data.checkout_url
        } else {
          this.receipt = response.data
          this.nextStep()
        }
      }, (errors) => {
        this.errorBag.store(errors.response.data)
      }).finally(() => {
        this.savingOrder = false
        this.displayCreateOrderModal = false
      })
    },
    removeFromBasket (item) {
      this.updatingItem = true
      let params = {
        add_to_basket: false // Change to 'Saved' status instead of 'Basket' status
      }

      v2Api.patch(`/custom-print-order-items/${item.id}`, params)
        .then(() => {
          this.loaded = false
          this.loadBasket()
        }, (errors) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Error',
            info: errors.response.data.message
          })
        }).finally(() => {
          this.updatingItem = false
        })
    },
    getPaymentTypeLabel (typeId) {
      if (typeId === 0) {
        return 'Invoice: Please invoice me (' + this.selectedOffice.name + ') directly for this print order, by adding it to my monthly invoice'
      }
      if (typeId === 1) {
        return 'Up-Front: Credit or Debit card payment, to be taken before order is placed'
      }
    },
    continueShopping () {
      this.$router.push('/print-order-form')
    },
    updatePhoneNumber (phoneNumber) {
      this.phoneNumber = phoneNumber
    }
  }
}
</script>

<style scoped>
input[type="radio"] {
  display: inline-block;
  width: 40px;
  height: 20px;
  font-size: 1.2rem;
  cursor: pointer;
}

input[type="radio"] + label {
  cursor: pointer;
  white-space: normal;
  line-height: 1.2rem;
}

input[type="radio"]:checked + label::before, input[type="radio"]:checked + label::after {
  content: "";
  display: none;
  border-width: 0px;
}

input[type="radio"] + label::before {
  content: '';
  display: none;
  width: 0px;
  height: 0px;
  border: 0px;
  padding: 0px;
}
</style>
